/**
 * Dashboard API service layer
 *
 * Each function maps to a backend endpoint and returns parsed data
 * for the corresponding dashboard tab/component.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Internal helpers ────────────────────────────────────────────────────────

async function authFetch(endpoint) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function authPost(endpoint, body) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function authPatch(endpoint, body) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function authDelete(endpoint) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// ═══════════════════════════════════════
// DASHBOARD TAB — Overview stats & charts
// ═══════════════════════════════════════

/**
 * Fetch dashboard overview stats (total applied, response rate, interviews, offers)
 * @returns {Promise<{total_applied: number, response_rate: number, interviews: number, offers: number}>}
 */
export async function getDashboardStats() {
  return authFetch('/api/dashboard/stats');
}

/**
 * Fetch weekly application counts for bar chart
 * @returns {Promise<Array<{week: string, applied: number, responses: number}>>}
 */
export async function getWeeklyApplications() {
  return authFetch('/api/dashboard/weekly-applications');
}

/**
 * Fetch response rate trend for line chart
 * @returns {Promise<Array<{week: string, rate: number}>>}
 */
export async function getResponseRateTrend() {
  return authFetch('/api/dashboard/response-rate-trend');
}

/**
 * Fetch application status breakdown for pie chart
 * @returns {Promise<Array<{name: string, value: number}>>}
 */
export async function getStatusBreakdown() {
  return authFetch('/api/dashboard/status-breakdown');
}

/**
 * Fetch companies with fastest response times
 * @returns {Promise<Array<{company: string, days: number}>>}
 */
export async function getFastestResponders() {
  return authFetch('/api/dashboard/fastest-responders');
}

// ═══════════════════════════════════════
// JOB FEED TAB — Scraped job listings
// ═══════════════════════════════════════

/**
 * Fetch job listings with optional filters
 * @param {Object} filters - { type?: string, visa?: boolean, salary_min?: number, tags?: string[], page?: number, limit?: number }
 * @returns {Promise<{jobs: Array, total: number, page: number}>}
 */
export async function getJobListings(filters = {}) {
  const params = new URLSearchParams();
  if (filters.type && filters.type !== 'All') params.append('type', filters.type);
  if (filters.visa) params.append('visa', 'true');
  if (filters.salary_min) params.append('salary_min', filters.salary_min);
  if (filters.tags?.length) params.append('tags', filters.tags.join(','));
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  return authFetch(`/api/jobs/list?${params.toString()}`);
}

/**
 * Quick apply to a job (creates application + triggers automation)
 * @param {number} jobId
 * @param {Object} options - { resume_id?: number, send_cold_email?: boolean }
 * @returns {Promise<{application_id: number, status: string}>}
 */
export async function quickApply(jobId, options = {}) {
  return authPost(`/api/jobs/apply/${jobId}`, options);
}

/**
 * Get AI-matched jobs based on user preferences
 * @returns {Promise<Array>}
 */
export async function getMatchedJobs() {
  return authFetch('/api/jobs/matches');
}

// ═══════════════════════════════════════
// PIPELINE TAB — Application tracking
// ═══════════════════════════════════════

/**
 * Get all applications grouped by status for the kanban board
 * @returns {Promise<{applied: Array, screened: Array, interview: Array, offer: Array}>}
 */
export async function getPipeline() {
  return authFetch('/api/pipeline/');
}

/**
 * Update application status (drag-and-drop between columns)
 * @param {number} applicationId
 * @param {string} newStatus - 'applied' | 'screened' | 'interview' | 'offer'
 * @returns {Promise<{success: boolean}>}
 */
export async function updateApplicationStatus(applicationId, newStatus) {
  return authPatch(`/api/pipeline/${applicationId}/status`, { status: newStatus });
}

/**
 * Get detailed view of a single application
 * @param {number} applicationId
 * @returns {Promise<Object>} - Full application with timeline, emails, cover letter
 */
export async function getApplicationDetail(applicationId) {
  return authFetch(`/api/pipeline/${applicationId}`);
}

/**
 * Delete/remove an application from the pipeline
 * @param {number} applicationId
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteApplication(applicationId) {
  return authDelete(`/api/pipeline/${applicationId}`);
}

// ═══════════════════════════════════════
// COLD EMAILS TAB — Templates & sending
// ═══════════════════════════════════════

/**
 * Get email templates (system defaults + user custom)
 * @returns {Promise<Array<{id: string, name: string, desc: string, body: string, is_system: boolean}>>}
 */
export async function getEmailTemplates() {
  return authFetch('/api/emails/templates');
}

/**
 * Preview a cold email filled with application data
 * @param {number} applicationId
 * @param {string} templateId
 * @returns {Promise<{to: string, subject: string, body: string}>}
 */
export async function previewEmail(applicationId, templateId) {
  return authPost('/api/emails/preview', { application_id: applicationId, template_id: templateId });
}

/**
 * Send a cold email
 * @param {Object} data - { application_id, template_id, provider: 'gmail'|'outlook', body, subject }
 * @returns {Promise<{email_id: number, status: string}>}
 */
export async function sendEmail(data) {
  return authPost('/api/emails/send', data);
}

/**
 * Schedule a cold email for later
 * @param {Object} data - { application_id, template_id, provider, body, send_at: ISO string }
 * @returns {Promise<{email_id: number, scheduled_at: string}>}
 */
export async function scheduleEmail(data) {
  return authPost('/api/emails/schedule', data);
}

/**
 * Get sent email history with tracking (opens, replies)
 * @returns {Promise<Array<{id, to, subject, status, sent_at, opened_at, replied_at}>>}
 */
export async function getEmailHistory() {
  return authFetch('/api/emails/history');
}

// ═══════════════════════════════════════
// RESUMES TAB — Resume management & cover letters
// ═══════════════════════════════════════

/**
 * Get user's uploaded resumes
 * @returns {Promise<Array<{id, name, file_url, tags, is_active, uses, match_rate, last_used, created_at}>>}
 */
export async function getResumes() {
  return authFetch('/api/resumes/');
}

/**
 * Upload a new resume (multipart)
 * @param {File} file - The resume file (PDF, DOCX, TXT)
 * @param {string} name - Display name for this resume version
 * @returns {Promise<{id: number, name: string, parsed_tags: string[]}>}
 */
export async function uploadResume(file, name) {
  const token = localStorage.getItem('access_token');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  const res = await fetch(`${API_URL}/api/resumes/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return res.json();
}

/**
 * Delete a resume
 * @param {number} resumeId
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteResume(resumeId) {
  return authDelete(`/api/resumes/${resumeId}`);
}

/**
 * Set a resume as the active/default resume
 * @param {number} resumeId
 * @returns {Promise<{success: boolean}>}
 */
export async function activateResume(resumeId) {
  return authPatch(`/api/resumes/${resumeId}/activate`, {});
}

/**
 * Generate an AI cover letter
 * @param {Object} data - { company: string, role: string, resume_id?: number }
 * @returns {Promise<{cover_letter: string, id: number}>}
 */
export async function generateCoverLetter(data) {
  return authPost('/api/resumes/cover-letter', data);
}

/**
 * Get saved cover letters
 * @returns {Promise<Array<{id, company, role, body, created_at}>>}
 */
export async function getCoverLetters() {
  return authFetch('/api/resumes/cover-letters');
}

// ═══════════════════════════════════════
// ACTIVITY TAB — Feed & notifications
// ═══════════════════════════════════════

/**
 * Get activity feed (all automation events)
 * @param {Object} params - { limit?: number, offset?: number, type?: string }
 * @returns {Promise<Array<{id, time, type, text, created_at}>>}
 */
export async function getActivityFeed(params = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.append('limit', params.limit);
  if (params.offset) qs.append('offset', params.offset);
  if (params.type) qs.append('type', params.type);
  return authFetch(`/api/activity/feed?${qs.toString()}`);
}

/**
 * Get user notifications
 * @returns {Promise<Array<{id, text, time, unread, type}>>}
 */
export async function getNotifications() {
  return authFetch('/api/activity/notifications');
}

/**
 * Mark a notification as read
 * @param {number} notificationId
 * @returns {Promise<{success: boolean}>}
 */
export async function markNotificationRead(notificationId) {
  return authPatch(`/api/activity/notifications/${notificationId}/read`, {});
}

/**
 * Mark all notifications as read
 * @returns {Promise<{success: boolean}>}
 */
export async function markAllNotificationsRead() {
  return authPost('/api/activity/notifications/read-all', {});
}

/**
 * Get email digest preference
 * @returns {Promise<{frequency: 'daily'|'weekly'|'off'}>}
 */
export async function getDigestPreference() {
  return authFetch('/api/activity/digest-preference');
}

/**
 * Update email digest preference
 * @param {'daily'|'weekly'|'off'} frequency
 * @returns {Promise<{success: boolean}>}
 */
export async function updateDigestPreference(frequency) {
  return authPatch('/api/activity/digest-preference', { frequency });
}

// ═══════════════════════════════════════
// PREFERENCES — User job search settings
// ═══════════════════════════════════════

/**
 * Get user's job search preferences
 * @returns {Promise<{roles, locations, min_pay, remote, cold_email_enabled, daily_volume, listing_freshness}>}
 */
export async function getPreferences() {
  return authFetch('/api/preferences/');
}

/**
 * Save/update user preferences
 * @param {Object} prefs - { roles, locations, min_pay, remote, cold_email_enabled, daily_volume, listing_freshness }
 * @returns {Promise<{success: boolean}>}
 */
export async function updatePreferences(prefs) {
  return authPost('/api/preferences/', prefs);
}
